import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import AxiosClient from '../../assets/AxiosClient';
import { useStateContext } from '../../Contexts/ContextProvider';

const SessionQuestions = () => {
  const { id } = useParams();
  const { SetNotification, API } = useStateContext();

  const [sessionTitle, setSessionTitle] = useState('');
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const perPage = 10;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sessRes, qRes] = await Promise.all([
        AxiosClient.get(`/sessions/${id}`),
        AxiosClient.get(`/sessions/${id}/questions`),
      ]);
      setSessionTitle(sessRes.data.title || '');
      setQuestions(qRes.data);
    } catch (error) {
      SetNotification('Error loading questions', 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateQuestion = async (q, payload) => {
    try {
      await AxiosClient.put(`/sessions/${id}/questions/${q.id}`, payload);
      SetNotification('Question updated', 'success');
      setQuestions((prev) =>
        prev.map((item) => (item.id === q.id ? { ...item, ...payload } : item))
      );
    } catch (error) {
      SetNotification('Error updating question', 'danger');
    }
  };

  const deleteQuestion = async (qid) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await AxiosClient.delete(`/sessions/${id}/questions/${qid}`);
      SetNotification('Question deleted', 'success');
      setQuestions((prev) => prev.filter((item) => item.id !== qid));
    } catch (error) {
      SetNotification('Error deleting question', 'danger');
    }
  };

  const qrSrc = (target) => `${API}/qr?text=${encodeURIComponent(target)}`;

  const downloadQr = async (target, filename) => {
    try {
      const res = await AxiosClient.get(qrSrc(target), { responseType: 'blob' });
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      SetNotification('Error downloading QR', 'danger');
    }
  };

  const filtered = questions.filter((q) => {
    if (filter === 'pending') return !q.is_active;
    if (filter === 'accepted') return q.is_active;
    return true;
  });

  const totalPages = Math.ceil(filtered.length / perPage);
  const current = filtered.slice((page - 1) * perPage, page * perPage);

  if (loading) return <h6 className="m-3">loading ..</h6>;

  return (
    <div className="m-4">
      <div className="mb-3">
        <Link to={`/sessions/${id}`} className="btn btn-outline-secondary btn-sm">
          ← Back to session
        </Link>
        <h3 className="mt-2">
          Questions {sessionTitle && <span className="text-muted">– {sessionTitle}</span>}
        </h3>
      </div>

      <div className="d-flex gap-2 mb-3">
        <span className="badge bg-secondary">Total: {questions.length}</span>
        <span className="badge bg-warning text-dark">
          Pending: {questions.filter((q) => !q.is_active).length}
        </span>
        <span className="badge bg-success">
          Accepted: {questions.filter((q) => q.is_active).length}
        </span>
      </div>

      <div className="btn-group mb-3" role="group">
        <button
          className={`btn btn-sm ${filter === 'all' ? 'btn-dark' : 'btn-outline-dark'}`}
          onClick={() => { setFilter('all'); setPage(1); }}
        >
          All
        </button>
        <button
          className={`btn btn-sm ${filter === 'pending' ? 'btn-warning' : 'btn-outline-warning'}`}
          onClick={() => { setFilter('pending'); setPage(1); }}
        >
          Pending
        </button>
        <button
          className={`btn btn-sm ${filter === 'accepted' ? 'btn-success' : 'btn-outline-success'}`}
          onClick={() => { setFilter('accepted'); setPage(1); }}
        >
          Accepted
        </button>
      </div>

      {questions.length === 0 ? (
        <div className="alert alert-light border text-center py-4">
          No questions yet. Share the QR code with the audience.
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-muted">No questions match this filter.</p>
      ) : (
        <>
          <div className="row g-3">
            {current.map((q) => (
              <div key={q.id} className="col-12">
                <div
                  className={`card border-0 shadow-sm ${
                    q.is_active
                      ? 'border-start border-success border-4'
                      : 'border-start border-secondary border-4'
                  }`}
                >
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <div>
                        <h6 className="mb-0 fw-semibold">{q.username}</h6>
                        <small className="text-muted">
                          {new Date(q.created_at).toLocaleString()}
                        </small>
                      </div>
                      <span className={`badge ${q.is_active ? 'bg-success' : 'bg-secondary'}`}>
                        {q.is_active ? 'Accepted' : 'Pending'}
                      </span>
                    </div>

                    <p className="mb-3 fs-6">{q.question}</p>

                    <div className="d-flex gap-2">
                      <button
                        className={`btn btn-sm ${
                          q.is_active ? 'btn-outline-secondary' : 'btn-success'
                        }`}
                        onClick={() => updateQuestion(q, { is_active: !q.is_active })}
                      >
                        {q.is_active ? 'Unaccept' : 'Accept'}
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => deleteQuestion(q.id)}
                      >
                        Delete
                      </button>
                      <button
                        className="btn btn-sm btn-outline-dark"
                        title="Download QR for this question"
                        onClick={() =>
                          downloadQr(
                            `https://ask.velorates.org/${id}#q-${q.id}`,
                            `question-${q.id}-qr.png`
                          )
                        }
                      >
                        QR
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3">
            <button
              className="btn btn-sm btn-secondary"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              Prev
            </button>
            <span>
              Page {page} of {totalPages || 1}
            </span>
            <button
              className="btn btn-sm btn-secondary"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SessionQuestions;
